import { faMicrophone, faPaperclip, faPhotoVideo, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { DirectUpload } from '@rails/activestorage';
import { useEffect, useState } from 'react';
import { AudioRecorder } from 'src/components/AudioRecorder';
import { Box } from 'src/components/Box';
import { DropdownMenu } from 'src/components/DropdownMenu';
import FileUploader from 'src/components/FileUploader';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { SafeArea } from 'src/components/SafeArea';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import {
  DeliveryMethod,
  ImageVideoPreviewUpload,
  MediaUpload,
} from 'src/models/conversation/_types';
import { getVideoUploadUrl, uploadVideo } from 'src/models/video/video';
import toast from 'src/utils/toast';

import { ScheduleMessageDialog } from './ScheduledMessages/ScheduleMessageDialog';
import { SendMessageButton } from './SendMessageButton';
import { UploadPreview } from './UploadPreview';
import {
  DEFAULT_UPLOAD_ERROR_MESSAGE,
  readDirectUploadErrorMessage,
} from './directUploadErrorMessage';

interface NewMessageInputProps {
  sendMessage: (options: {
    newMessage: string;
    files: MediaUpload[];
    sendAt?: Date | null;
    delivery?: DeliveryMethod[];
    subject?: string;
  }) => void;
  onInputChange?: (message: string) => void;
  onScheduleMessage?: (date: Date) => void;
  defaultMessage?: string;
  defaultDelivery?: DeliveryMethod[];
  hasRecipient?: boolean;
  disableSendButton?: boolean;
}

export const NewMessageInput = ({
  sendMessage,
  onInputChange,
  onScheduleMessage,
  defaultMessage,
  defaultDelivery = ['in-app', 'email'],
  hasRecipient = false,
  disableSendButton = false,
}: NewMessageInputProps) => {
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [newMessage, setNewMessage] = useState(defaultMessage || '');

  useEffect(() => {
    setNewMessage(defaultMessage || '');
  }, [defaultMessage]);

  const [focused, setFocused] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachmentDropdownOpen, setAttachmentDropdownOpen] = useState(false);

  const [uploads, setUploads] = useState<ImageVideoPreviewUpload[]>([]);
  const [files, setFiles] = useState<MediaUpload[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [sendAt, setSendAt] = useState<Date | null>(null);
  const [delivery, setDelivery] = useState<DeliveryMethod[]>(defaultDelivery);
  const [subject, setSubject] = useState('');
  // even though this isn't used, removing it breaks the audio recorder
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isRecording, setIsRecording] = useState(false);

  const { user } = useSession();

  useEffect(() => {
    setDelivery(defaultDelivery);
  }, [defaultDelivery]);

  const sendingDisabled =
    !hasRecipient ||
    (!newMessage.trim() && !uploads.length) ||
    uploading ||
    disableSendButton;

  const onClickSendMessage = (options: {
    message: string;
    sendAt: Date | null;
    delivery?: DeliveryMethod[];
    subject?: string;
  }) => {
    if (sendingDisabled) {
      return;
    }
    sendMessage({
      newMessage: options.message,
      files,
      sendAt: options.sendAt,
      delivery: options.delivery ?? delivery,
      subject: options.subject ?? subject,
    });
    if (options.sendAt && onScheduleMessage) {
      onScheduleMessage(options.sendAt);
    }
    setNewMessage('');
    setUploads([]);
    setFiles([]);
    setSendAt(null);
    setDelivery(options.delivery ?? delivery);
    setSubject('');
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const sendMessageOnEnter = (e) => {
    if (e.shiftKey && e.key === 'Enter') {
      return;
    }
    if (e.key === 'Enter' && !smDown) {
      e.preventDefault();
      onClickSendMessage({
        message: e.target.value,
        sendAt,
        delivery,
        subject,
      });
    }
  };

  const handleCancelClick = (upload) => {
    setUploads([...uploads.filter((f) => f !== upload)]);
    setFiles([...files.filter((f) => f.id !== upload.file)]);
  };

  const handleFileSelect = async (selectedFiles) => {
    const uploadedFiles: MediaUpload[] = [];

    setUploading(true);
    setUploads(() =>
      selectedFiles.map((file) => {
        return {
          file,
          progress: 0,
          preview:
            file.type.startsWith('image/') || file.type.startsWith('video/')
              ? URL.createObjectURL(file)
              : file.name,
        };
      })
    );
    setAttachmentDropdownOpen(false);

    await Promise.all(
      selectedFiles.map((file) => {
        // Generate a preview if the file is an image
        if (file.type.startsWith('image/')) {
          return handleFileUpload(file).then((fileId: string) => {
            uploadedFiles.push({ id: file, type: 'image', key: fileId });
          });
        } else if (file.type.startsWith('video/')) {
          return handleVideoUpload(file).then(({ videoId, id }) => {
            uploadedFiles.push({
              id: file,
              type: 'video',
              key: videoId,
              match: id,
            });
          });
          //normal file
        } else {
          return handleFileUpload(file).then((fileId: string) => {
            uploadedFiles.push({ id: file, type: 'file', key: fileId });
          });
        }
      })
    )
      .then(() => setFiles(uploadedFiles))
      .catch((error) => {
        toast.error(String(error || DEFAULT_UPLOAD_ERROR_MESSAGE));
        setUploads([]);
        setFiles([]);
      })
      .finally(() => setUploading(false));
  };

  const handleVideoUpload = async (file) => {
    const muxUploadUrl = await getVideoUploadUrl({ videoType: 'Message' });
    const { new_asset_settings, url, id } = muxUploadUrl.data;
    const videoId = new_asset_settings.passthrough;
    await uploadVideo({
      url: url,
      file,
      progressCallback: (progress) => {
        setUploads((prevFiles) => {
          const files = [...prevFiles];
          const updatedFile = files.find((f) => f.file === file);

          if (updatedFile) {
            updatedFile.progress = Math.round(progress);
          }

          return files;
        });
      },
      errorCallback: () => toast.error('Failed to upload video'),
    });

    return { videoId, id };
  };

  const handleFileUpload = (file): Promise<string> => {
    let blobCreateXhr: XMLHttpRequest | null = null;
    const upload = new DirectUpload(file, '/api/v4/direct_uploads', {
      directUploadWillCreateBlobWithXHR: (xhr) => {
        blobCreateXhr = xhr;
        xhr.setRequestHeader('API-TOKEN', user.authToken);
      },
      directUploadWillStoreFileWithXHR(request) {
        request.upload.addEventListener('progress', (event) =>
          this.directUploadDidProgress(event)
        );
      },
      directUploadDidProgress(event) {
        setUploads((prevFiles) => {
          const files = [...prevFiles];
          const updatedFile = files.find((f) => f.file === file);

          if (updatedFile) {
            updatedFile.progress = Math.round(
              (event.loaded / event.total) * 100
            );
          }
          return files;
        });
      },
    });

    return new Promise(function executor(resolve, reject) {
      upload.create((error, blob) => {
        if (error) {
          const validationMessage =
            blobCreateXhr && blobCreateXhr.status >= 400
              ? readDirectUploadErrorMessage(blobCreateXhr)
              : null;
          reject(validationMessage || error);
        } else {
          resolve(blob.signed_id);
        }
      });
    });
  };

  const onAudioStart = () => {
    setIsRecording(true);
  };

  const onAudioStop = (recordingBlob) => {
    setUploading(true);
    setIsRecording(false);
    const file = new File([recordingBlob], 'memo.mp3', {
      type: recordingBlob.type,
    });

    setUploads([
      ...uploads,
      {
        file: file,
        progress: 0,
        preview: URL.createObjectURL(recordingBlob),
      },
    ]);
    handleFileUpload(file)
      .then((fileId: string) => {
        const nonAudioFiles = files.filter((f) => f.type != 'audio');
        setFiles([...nonAudioFiles, { id: file, type: 'audio', key: fileId }]);
      })
      .catch((error) => {
        toast.error(String(error || DEFAULT_UPLOAD_ERROR_MESSAGE));
        setUploads((prevUploads) =>
          prevUploads.filter((upload) => upload.file !== file)
        );
      })
      .finally(() => setUploading(false));
    setAttachmentDropdownOpen(false);
  };

  const handleChange = (e) => {
    if (onInputChange) onInputChange(e);
    setNewMessage(e.target.value);
  };

  const handleScheduleSubmit = (scheduledTime: Date) => {
    setModalOpen(false);
    onClickSendMessage({
      message: newMessage,
      sendAt: scheduledTime,
      delivery,
      subject,
    });
  };

  return (
    <SafeArea bottom>
      <Stack
        spacing={smDown ? 1 : 2}
        direction="row"
        sx={{
          px: smDown ? 1 : 2,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <DropdownMenu
          closeOnContentClick={false}
          open={attachmentDropdownOpen}
          setOpen={setAttachmentDropdownOpen}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          trigger={
            <IconButton
              onClick={() => setAttachmentDropdownOpen(true)}
              sx={{
                borderRadius: '50%',
                bgcolor: 'secondary.alpha8',
              }}
            >
              <Icon
                size="small"
                icon={faPlus}
              />
            </IconButton>
          }
        >
          <AudioRecorder
            onStart={onAudioStart}
            onEnd={onAudioStop}
            trigger={
              <ListItem
                clickable
                primary="Voice Recording"
                icon={faMicrophone}
              ></ListItem>
            }
          />
          <FileUploader
            trigger={
              <ListItem
                clickable
                primary="Media Upload"
                icon={faPhotoVideo}
              ></ListItem>
            }
            onFileSelect={handleFileSelect}
            accept="image/*,video/*"
          />
          <FileUploader
            trigger={
              <ListItem
                clickable
                primary="File Upload"
                icon={faPaperclip}
              ></ListItem>
            }
            onFileSelect={handleFileSelect}
            accept="text/plain,application/pdf"
          />
        </DropdownMenu>
        <Box
          sx={{
            width: '100%',
            outline: focused
              ? '1px solid var(--mui-palette-primary-main)'
              : '1px solid var(--mui-palette-OutlinedInput-border)',
            outlineWidth: focused ? 2 : 1,
            borderRadius: 1,
          }}
        >
          <TextField
            variant="standard"
            placeholder="Message"
            value={newMessage}
            onChange={handleChange}
            onKeyDown={sendMessageOnEnter}
            multiline
            minRows={1}
            maxRows={6}
            autoFocus={!smDown}
            sx={{ pt: 0.1 }}
            InputProps={{
              onFocus: handleFocus,
              onBlur: handleBlur,
            }}
          />
          <UploadPreview
            uploads={uploads}
            uploading={uploading}
            handleCancelClick={handleCancelClick}
          />
        </Box>
        {!sendingDisabled && (
          <SendMessageButton
            user={user}
            sendingDisabled={sendingDisabled}
            onClickSendMessage={onClickSendMessage}
            newMessage={newMessage}
            sendAt={sendAt}
            setSendAt={setSendAt}
            setModalOpen={setModalOpen}
            delivery={delivery}
            setDelivery={setDelivery}
            subject={subject}
            setSubject={setSubject}
          />
        )}
      </Stack>
      <ScheduleMessageDialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSendAt(null);
        }}
        onSubmit={handleScheduleSubmit}
      />
    </SafeArea>
  );
};
